import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { cn } from "../../utils/utils";
import { VideoPlayer } from "../../components/common/VideoPlayer"

import { CalendarIconFilled, BusinessCardIconFilled, CheckIcon } from '../../components/common/Icons'
import LanguageSelector from '../../components/common/LanguageSelector';
import { getPreferredMobileStoreTargets, MOBILE_APP_STORE_VERSION, MOBILE_STORE_LINKS } from '../../config/mobileStores';
import { getPublicPageContent, normalizePublicLanguage } from './publicPageContent';

const BRAND_LOGO = '/logos/logo-alta-branca.png?v=20260520';
export const LANDING_LANGUAGE_OVERRIDE_KEY = 'clarityfiles:landingLanguageOverride';

const landingFontStyle = {
  fontFamily: "Aptos, 'Segoe UI', Inter, Roboto, Arial, sans-serif",
};

const STORE_BADGE_ASSETS = {
  'pt-BR': {
    ios: '/store-badges/app-store-pt-BR.svg',
    android: '/store-badges/google-play-pt-BR.svg',
  },
  en: {
    ios: '/store-badges/app-store-en.svg',
    android: '/store-badges/google-play-en.svg',
  },
  es: {
    ios: '/store-badges/app-store-es.svg',
    android: '/store-badges/google-play-es.svg',
  },
};

export const normalizeLandingLanguage = (language) => {
  const value = language?.toLowerCase() || '';
  if (value.startsWith('pt')) return 'pt-BR';
  if (value.startsWith('es')) return 'es';
  return 'en';
};

const getBrowserLandingLanguage = () => {
  if (typeof navigator === 'undefined') return 'en';

  const primaryLanguage = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages[0]
    : navigator.language;

  return normalizeLandingLanguage(primaryLanguage);
};

const getLandingLanguageOverride = () => {
  try {
    return localStorage.getItem(LANDING_LANGUAGE_OVERRIDE_KEY);
  } catch {
    return null;
  }
};

const setLandingLanguageOverride = (language) => {
  try {
    localStorage.setItem(LANDING_LANGUAGE_OVERRIDE_KEY, language);
  } catch {
    // Se o navegador bloquear localStorage, a troca manual ainda vale na sessao atual.
  }
};

const StoreBadge = ({ type, copy, locale }) => (
  <a href={MOBILE_STORE_LINKS[type]}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex h-12 w-40 shrink-0 items-center justify-center overflow-hidden rounded-lg transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-clarity-400 focus:ring-offset-2"
  >
    <img src={STORE_BADGE_ASSETS[locale][type]} alt={copy.aria} className="h-10 w-auto max-w-full object-contain" loading="lazy" />
  </a>
);

const StoreBadges = ({ stores, badges, locale }) => (
  <div className="flex flex-wrap items-center justify-center">
    {stores.map((store) => (
      <StoreBadge key={store} type={store} copy={badges[store]} locale={locale} />
    ))}
  </div>
);

const Card = ({ children, className }) => (
  <div className={cn(
    "w-full rounded-[3rem]",
    "shadow-strong",
    "px-4 pt-14 pb-28 sm:pt-16 sm:pb-20", className)}>
    {children}
  </div>
);

const Pill = ({ children, className }) => (
  <div className={cn(
    "inline-flex items-center justify-center w-fit min-w-72 md:min-w-56 lg:min-w-72 min-h-9",
    "rounded-full px-5 py-2 md:px-2 md:py-1 lg:px-5 lg:py-2 shadow-xl",
    "text-lg md:text-sm lg:text-lg font-bold",
    "bg-clarity-250 text-clarity-900", className)}>
    {children}
  </div>
);

const StepCard = ({ number, title, text, className }) => (
  <div className={cn("relative flex flex-col h-full pt-8", className)}>
    <div className="absolute left-1/2 top-0 z-10 -translate-x-1/2">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-2xl font-bold text-slate-900 shadow-md">
        {number}
      </div>
    </div>
    <div className="flex h-full flex-col items-center max-w-xs sm:max-h-[26rem] rounded-3xl bg-slate-700 px-6 pt-12 pb-10 text-center text-white">
      <h1 className="min-h-16 mb-8 text-2xl font-bold">
        {title}
      </h1>

      <div className="text-slate-300 leading-relaxed">
        <p className="tracking-wide text-center text-white text-sm lg:text-lg mx-2">
          {text}
        </p>
      </div>
    </div>
  </div>
);

const PricingCard = ({ name, price, description, features, highlighted = false, landing, className }) => (
  <div className={cn("relative flex flex-col rounded-3xl sm:p-8 p-6",
    highlighted ? "border-2 border-blue-400 bg-slate-800" : "border-2 border-slate-500 bg-slate-800", className
  )}>
    {highlighted && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
        <div className="flex items-center gap-1.5 rounded-full bg-blue-400 px-4 py-1.5 text-sm font-bold text-white">
          <span>★</span>
          <span>{landing.mostPopularPlan}</span>
        </div>
      </div>
    )}
    <h3 className={cn("mt-6 xs:mt-1 text-3xl font-bold", highlighted ? "text-blue-400" : "text-white")}>
      {name}
    </h3>
    <div className="mt-6">
      <p className="text-sm text-slate-400">{landing.pricesFrom}</p>
      <div className="mt-1 flex items-baseline gap-1">
        <span className={cn("text-4xl font-bold tracking-tight", highlighted ? "text-blue-400" : "text-white")}>
          {price}
        </span>
        <span className="text-lg text-slate-400">/{landing.perMonth}</span>
      </div>
    </div>
    <p className="min-h-12 lg:min-h-0 mt-6 text-sm leading-relaxed text-slate-400">
      {description}
    </p>
    <ul className="w-full mt-8 space-y-4 border-t border-slate-700 pt-8">
      {features.map((feature) => (
        <li key={feature} className="flex items-center gap-3">
          <CheckIcon className={cn(
            "h-5 w-5 flex-shrink-0",
            highlighted ? "text-blue-400" : "text-emerald-400"
          )} />
          <span className="text-sm text-slate-200">{feature}</span>
        </li>
      ))}
    </ul>
    <div className="mt-10">
      <Link
        to="/register"
        className={cn(
          "block w-full self-stretch px-6 rounded-full text-center py-3.5 text-sm font-semibold transition-colors",
          highlighted
            ? "bg-blue-500 text-white hover:bg-blue-400"
            : "bg-white text-blue-600 hover:bg-slate-100"
        )} >
        {landing.startFree}
      </Link>
    </div>
  </div>
);

const FeatureBlock = ({ title, items }) => (
  <div>
    <div className="flex items-center gap-4 mb-6 mt-6">
      <div className="relative flex h-8 w-8 shrink-0 items-center justify-center">
        <div className="absolute h-7 w-7 rounded-full bg-blue-500/40 blur-md" />
        <div className="h-3 w-3 rounded-full bg-blue-400" />
      </div>

      <h3 className="text-2xl md:text-3xl font-semibold mb-4">{title}</h3>
    </div>
    <div className="relative ml-4 pl-8">
      <ul className="space-y-4">
        {items.map((item) => (
          <li key={item} className="relative pl-6">
            <div className="absolute left-0 top-1 bottom-1 w-px bg-slate-400/50" />
            <span className="text-clarity-900 text-lg">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const PrimaryButton = ({ text }) => (
  <Link
    to="/register"
    className="rounded-xl bg-clarity-500 px-12 py-2 shadow-2xl text-white text-center font-bold text-xl mb-12 hover:bg-clarity-600">
    {text}
  </Link>
);

const LandingPage = () => {
  const { i18n } = useTranslation();
  const content = getPublicPageContent(i18n.language);
  const { nav, landing, footer } = content;
  const currentLocale = normalizePublicLanguage(i18n.language);
  const visibleStores = useMemo(() => getPreferredMobileStoreTargets(), []);

  useEffect(() => {
    const manualLanguage = getLandingLanguageOverride();
    const preferredLanguage = manualLanguage
      ? normalizeLandingLanguage(manualLanguage)
      : getBrowserLandingLanguage();

    if (normalizePublicLanguage(i18n.language) !== preferredLanguage) {
      i18n.changeLanguage(preferredLanguage);
    }
  }, [i18n, i18n.language]);

  useEffect(() => {
    document.title = landing.metaTitle;
  }, [landing.metaTitle]);

  return (
    <div className="min-h-screen bg-slate-200 text-slate-900 antialiased" style={landingFontStyle}>
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-clarity-900 backdrop-blur">
        <div className="mx-auto flex gap-3 min-h-24 max-w-7xl items-center justify-between px-1 sm:px-5 py-3">
          <Link to="/" className="flex shrink-0 items-center" aria-label="Clarity Files">
            <img src={BRAND_LOGO} alt="Clarity Files" className="h-[72px] w-auto max-w-[165px] object-contain" />
          </Link>
          <nav className="hidden md:flex items-center gap-6 lg:gap-10 text-md font-medium text-white">
            <a href="#inicio" className="hidden lg:flex hover:text-clarity-600">{nav.home}</a>
            <a href="#sobre" className="hover:text-clarity-600">{nav.about}</a>
            <a href="#planos" className="hover:text-clarity-600">{nav.plans}</a>
            <a href="#tutoriais" className="hidden lg:flex hover:text-clarity-600">{nav.tutorials}</a>
            <a href="#contato" className="hover:text-clarity-600">{nav.contact}</a>
          </nav>
          <div className="flex items-center sm:gap-3">
            <Link to="/login" className="hidden rounded-xl px-4 py-2 text-sm font-semibold text-white hover:bg-clarity-700 sm:inline-flex">
              {nav.login}
            </Link>
            <Link to="/register" className="mr-2 lg:mr-20 rounded-xl bg-clarity-500 px-4 py-2 text-sm font-bold text-white text-center shadow-sm transition hover:bg-clarity-600">
              {nav.freeTrial}
            </Link>
            <LanguageSelector showLabel={false} className="shrink-0" onLanguageChange={setLandingLanguageOverride} />
          </div>
        </div>
      </header>
      <main>
        <section id="inicio" className="overflow-hidden bg-slate-200 px-3 pt-2 sm:pt-3 pb-20 sm:px-4 font">
          <Card className="bg-gradient-to-b from-clarity-500 to-clarity-900 flex flex-col items-center">
            <h1 className="mx-auto max-w-5xl mt-5 sm:mt-8 text-center text-3xl font-extrabold tracking-tight text-white md:text-5xl">
              {landing.heroTitle}
            </h1>
            <h2 className="mx-auto mt-4 max-w-4xl tracking-wide text-center text-base text-white md:text-2xl">
              {landing.heroSubtitle}
            </h2>
            <div className="mt-10 flex justify-center">
              <Link to="/register" className="inline-flex items-center justify-center rounded-2xl bg-clarity-500 px-7 py-3 text-md font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-clarity-600">
                {nav.freeTrial} <span className="ml-2">→</span>
              </Link>
            </div>
            <p className="mx-auto mt-2 max-w-4xl mb-8 text-center text-sm text-white/90 md:text-lg">
              {landing.getStartedSubtitle}
            </p>
            <StoreBadges stores={visibleStores} badges={landing.storeBadges} locale={currentLocale} />
            <p className="text-xs font-medium text-slate-400 mb-16 sm:mb-48">
              {landing.latestAppVersion.replace('{{version}}', MOBILE_APP_STORE_VERSION)}
            </p>
          </Card>
          <div className="z-10 mx-auto -mt-28 w-full max-w-4xl sm:-mt-56 px-2">
            <div className="overflow-hidden rounded-2xl aspect-[16/9] sm:aspect-[16/8] shadow-2xl border border-white/10">
              <VideoPlayer language={currentLocale} />
            </div>
          </div>
        </section>
        <section id="sobre" className="overflow-hidden">
          <div className="flex flex-col items-center w-full px-10 lg:px-1">
            <h1 className="mx-auto max-w-6xl md:mt-10 text-center text-xl font-bold text-clarity-900 md:text-4xl">
              {landing.whatIsTitle}
            </h1>
            <p className="mx-auto mt-10 text-center text-xl font-[300] text-clarity-900 md:text-2xl">
              {landing.whatIsText1}
            </p>
            <p className="mx-auto max-w-6xl mt-10 text-center text-xl text-clarity-900 md:text-2xl">
              {landing.whatIsText2} <span className="font-extrabold text-clarity-900">{landing.whatIsText2Highlighted}</span>
            </p>
            <div className="max-w-7xl rounded-3xl bg-clarity-250 shadow-strong mt-10 mb-10 pt-6 pb-12 px-1">
              <p className="mt-4 tracking-wide text-center text-white text-xl md:text-2xl mx-10">
                {landing.whatIsCardText}
              </p>
            </div>
          </div>
        </section>
        <section id="planos" className="overflow-hidden bg-slate-200 px-3 pt-4 pb-20 lg:px-4 font">
          <Card className="flex flex-col items-center bg-emerald-50/20 -mb-16">
            <h1 className="text-2xl md:text-4xl font-bold mb-16">
              {landing.forWhoTitle}
            </h1>
            <div className="space-y-1 md:space-y-6 lg:space-y-10">
              <div className="flex flex-col sm:flex-row justify-center sm:items-center gap-y-2 mb-2 gap-6 lg:gap-12">
                <Pill>{landing.forWhoItem1}</Pill>
                <Pill>{landing.forWhoItem2}</Pill>
                <Pill>{landing.forWhoItem3}</Pill>
              </div>
              <div className="flex flex-col sm:flex-row justify-center md:items-center gap-y-2 gap-6 lg:gap-6 pb-1">
                <Pill>{landing.forWhoItem4}</Pill>
                <Pill>{landing.forWhoItem5}</Pill>
              </div>
              <div className="flex flex-col sm:flex-row justify-center md:items-center gap-y-2 gap-6 lg:gap-12 md:pb-16">
                <Pill>{landing.forWhoItem6}</Pill>
                <Pill>{landing.forWhoItem7}</Pill>
                <Pill>{landing.forWhoItem8}</Pill>
              </div>
            </div>
          </Card>
          <Card className="flex flex-col items-center bg-gradient-to-b from-clarity-500 to-clarity-900">
            <div className="flex flex-col items-center">
              <h1 className="px-2 md:px-0 text-2xl md:text-4xl font-bold text-center text-white mb-12">
                {landing.chooseIdealPlan}
              </h1>
              <h3 className="mx-auto text-center text-xl font-[300] text-white md:text-2xl">
                {landing.startFreeToday}
              </h3>
              <h3 className="mx-auto mb-12 text-center text-xl font-[300] text-white md:text-2xl">
                {landing.testAndChoose}
              </h3>
              <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 md:grid-cols-2">
                <PricingCard
                  name={landing.basicPlan}
                  price={landing.basicPrice}
                  description={landing.basicDescription}
                  features={[
                    landing.basicFeature1,
                    landing.basicFeature2,
                    landing.basicFeature3
                  ]}
                  landing={landing}
                />

                <PricingCard
                  name={landing.standardPlan}
                  price={landing.standardPrice}
                  description={landing.standardDescription}
                  features={[
                    landing.standardFeature1,
                    landing.standardFeature2,
                    landing.standardFeature3
                  ]}
                  highlighted
                  landing={landing}
                />
              </div>
              <p className="mx-auto max-w-4xl mt-10 text-center text-xl font-normal text-white md:text-sm">
                {landing.readyToStartText3}
              </p>
            </div>
          </Card>
          <div className="flex flex-col items-center">
            <h1 className="text-3xl md:text-4xl font-bold mt-32 text-clarity-900 ">
              {landing.howToStart}
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <StepCard number={1} title={landing.howToStartStep1Title} text={landing.howToStartStep1} />
              <StepCard number={2} title={landing.howToStartStep2Title} text={landing.howToStartStep2} />
              <StepCard number={3} title={landing.howToStartStep3Title} text={landing.howToStartStep3} />
            </div>
            <div className="mt-16 flex flex-col items-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-10 text-clarity-900 text-center">
                {landing.howToStartTitle2}
              </h1>
              <PrimaryButton text={nav.freeTrial} />
              <p className="mx-auto mb-10 max-w-4xl text-center text-sm text-clarity-900/90 md:text-lg">
                {landing.getStartedSubtitle}
              </p>
              <StoreBadges stores={visibleStores} badges={landing.storeBadges} locale={currentLocale} />
              <p className="text-xs font-medium text-slate-400">
                {landing.latestAppVersion.replace('{{version}}', MOBILE_APP_STORE_VERSION)}
              </p>
            </div>
          </div>
        </section>
        <section id="tutoriais" className="px-4 sm:px-6 lg:px-8">
          <Card className="flex flex-col items-center bg-white -mb-16 px-12 lg:px-4">
            <h1 className="mx-auto max-w-6xl md:mt-10 text-center text-3xl font-bold text-clarity-900 md:text-4xl">
              {landing.featuresTitle}
            </h1>
            <div className="max-w-6xl md:max-w-5xl mx-auto mt-8">
              <FeatureBlock title={landing.inProgressFeatureTitle}
                items={[
                  landing.inProgressFeatureItem1,
                  landing.inProgressFeatureItem2,
                  landing.inProgressFeatureItem3
                ]}
              />
              <FeatureBlock title={landing.financialFeatureTitle} items={[landing.financialFeatureItem]} />
              <FeatureBlock title={landing.hoursFeatureTitle} items={[landing.hoursFeatureItem]} />
              <FeatureBlock title={landing.reportsFeatureTitle} items={[landing.reportsFeatureItem]} />
              <h1 className="mx-auto max-w-6xl mt-16 md:mt-24 mb-16 md:mb-24 text-center text-3xl font-bold text-clarity-900 md:text-4xl">
                {landing.thereIsMore}
              </h1>
              <div className="mx-auto flex w-full justify-center">
                <div className="grid w-full max-w-3xl grid-cols-1 justify-items-center gap-y-10 md:grid-cols-2 md:gap-x-10">
                  <Card className="flex h-full w-full max-w-72 flex-col items-center bg-clarity-500 md:max-w-96">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center md:h-16 md:w-16">
                      <BusinessCardIconFilled className="h-full w-full text-white" />
                    </div>

                    <h3 className="mb-6 mt-5 flex min-h-[3rem] items-center px-4 text-center text-lg font-bold leading-tight text-white md:mt-6 md:min-h-[4.5rem] md:text-2xl">
                      {landing.digitalCardTitle}
                    </h3>
                    <p className="mx-5 min-h-[4rem] text-justify text-sm leading-snug text-white md:min-h-[4.5rem] md:text-base">
                      {landing.digitalCardText}
                    </p>
                  </Card>
                  <Card className="flex h-full w-full max-w-72 flex-col items-center bg-clarity-500 md:max-w-96">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center md:h-16 md:w-16">
                      <CalendarIconFilled className="h-full w-full text-white" />
                    </div>

                    <h3 className="mb-6 mt-5 flex min-h-[3rem] items-center px-4 text-center text-lg font-bold leading-tight text-white md:mt-6 md:min-h-[4.5rem] md:text-2xl">
                      {landing.calendarEventsTitle}
                    </h3>
                    <p className="mx-5 min-h-[4rem] text-justify text-sm leading-snug text-white md:min-h-[4.5rem] md:text-base">
                      {landing.calendarEventsText}
                    </p>
                  </Card>
                </div>
              </div>
            </div>
          </Card>
          <div className="px-4 flex flex-col items-center mt-32">
            <h1 className="mx-auto max-w-6xl md:mt-10 mb-10 text-center text-3xl font-bold text-clarity-900 md:text-4xl">
              {landing.readyToStartTitle}
            </h1>
            <h2 className="mx-auto max-w-4xl mt-10 text-center text-xl font-normal text-clarity-900 md:text-2xl">
              {landing.readyToStartText1}
              <span className="font-bold">{landing.readyToStartText2}</span>
              {landing.readyToStartText3}
            </h2>
            <h2 className="mx-auto max-w-4xl mt-10 mb-10 text-center text-xl font-normal text-clarity-900 md:text-2xl">
              {landing.terciaryCta}
            </h2>
            <PrimaryButton text={landing.storeDownloadTitle2} />
            <StoreBadges stores={visibleStores} badges={landing.storeBadges} locale={currentLocale} />
            <p className="text-xs font-medium text-slate-400">
              {landing.latestAppVersion.replace('{{version}}', MOBILE_APP_STORE_VERSION)}
            </p>
          </div>
        </section >
      </main >
      <footer id="contato" className="mt-10 mb-1">
        <Card className="bg-gradient-to-b from-clarity-500 to-clarity-900 flex flex-col sm:flex-row justify-center pt-6 pb-8 sm:pt-16 sm:pb-20">
          <div className="mx-auto flex max-w-6xl flex-col sm:flex-row items-center gap-6 px-5 text-xs text-white md:flex-row">
            <img src={BRAND_LOGO} alt="Clarity Files" className="h-16 w-auto max-w-[142px] object-contain" />
            <div className="flex flex-wrap items-center justify-center gap-x-6">
              <a href={`mailto:${footer.contact}`} className="hover:text-clarity-600">{footer.contact}</a>
              <Link to="/terms" className="hover:text-clarity-600">{nav.terms}</Link>
            </div>
            <p>{footer.copyright}</p>
          </div>
        </Card>
      </footer>
    </div >
  );
};

export default LandingPage;
